// UN MESSAGE D'ERREUR NE CASSE PAS L'ÉCRAN — la seule rédaction de la règle.
//
// LE DÉFAUT. FastAPI répond `detail` de DEUX façons incompatibles :
//
//   · nos refus métier      → une PHRASE   ("Ce mot est déjà validé.")
//   · une validation Pydantic (422) → une LISTE d'objets :
//        [{ type: "string_too_long", loc: ["body","carrier"],
//           msg: "String should have at most 1000 characters", input: "x" }]
//
// Poser la seconde dans du JSX ne donne pas un vilain message : React refuse
// de rendre un objet (« Objects are not valid as a React child ») et l'arbre
// tombe. Une phrase trop longue EFFAÇAIT donc l'écran au lieu de dire pourquoi
// elle était refusée — chez l'invité comme dans le panneau d'administration.
//
// ⚠ ET ON NE FABRIQUE PAS UN MESSAGE À PARTIR DU 422. Concaténer les `msg` du
// validateur afficherait « String should have at most 1000 characters » sous
// les yeux d'un invité français : du charabia technique, en anglais, écrit par
// une bibliothèque tierce. C'est ce que la vague #847 vient d'interdire sur
// l'autre porte (le message BRUT du fournisseur ne voyage pas jusqu'au
// navigateur). D'où le contrat ci-dessous : on rend `null`, et l'APPELANT pose
// son propre repli — dans sa langue, en disant ce qu'on peut faire.
//
// ⚠ DEUX FORMES D'ERREUR, une seule règle. La maison a deux clients HTTP et
// ils ne rangent pas le corps au même endroit :
//   · axios              → `err.response.data.detail`
//   · le `call()` de `lib/chantier.js` (fetch) → `err.data.detail` (:1625)
// Les lire tous les deux ICI est ce qui permet à `ChantierBoard` d'utiliser le
// même helper que les autres. Deux rédactions divergeraient — c'est la faute
// que la vague #843 a fermée, on ne la rouvre pas pour un `?.` de différence.

/** La valeur brute de `detail`, quel que soit le client HTTP. */
function rawDetail(err) {
    if (!err) return undefined;
    const fromAxios = err.response && err.response.data ? err.response.data.detail : undefined;
    if (fromAxios !== undefined) return fromAxios;
    return err.data ? err.data.detail : undefined;
}

/**
 * Le `detail` d'une erreur HTTP — et SEULEMENT s'il est lisible par un humain.
 *
 * @returns {string|null} la phrase que NOUS avons écrite, ou `null` quand il
 *   n'y en a pas (liste de validation, objet seul, nombre, absent). `null` est
 *   un résultat normal : l'appelant enchaîne sur son propre repli.
 */
export function detailText(err) {
    const d = rawDetail(err);
    return typeof d === 'string' && d.trim() ? d : null;
}

/**
 * La même règle, pour une réponse demandée en `responseType: "blob"`.
 *
 * ⚠ ET C'EST LE CORPS ENTIER QUI EST UN BLOB, PAS LE `detail`. La première
 * rédaction cherchait `err.response.data.detail instanceof Blob` — une forme
 * qu'axios n'émet jamais. Avec `responseType: "blob"`, son `transformResponse`
 * ne parse RIEN (il ne `JSON.parse` que des chaînes, et seulement en `json`) :
 * `err.response.data` **EST** le Blob, et `.detail` y vaut `undefined`.
 *
 * La branche était donc MORTE, et ses quatre tests verts portaient sur une
 * forme inventée. Mesuré avant de réécrire :
 *
 *     { response: { data: blob } }            → null   ← la vraie
 *     { response: { data: { detail: blob } } } → la phrase  ← l'imaginaire
 *
 * Ce que ça coûtait, et c'est pour ça que la revue l'a mis en bloquant : les
 * deux seuls écrans concernés (`PronunciationEditor`, `PronunciationPending`)
 * ne lisaient JAMAIS la phrase du serveur. Donc ni « Raccourcissez la phrase
 * d'essai » (le plafond de dépense posé la veille), ni la distinction entre un
 * quota épuisé et une limite de débit — tout le propos de la vague #847, qui
 * existe précisément pour ne pas envoyer un opérateur vérifier un compteur
 * intact. Deux nuits de travail qui n'atteignaient pas l'écran.
 *
 * Un Blob illisible — de l'audio, du HTML, un JSON sans `detail` — n'est pas
 * une erreur de plus : c'est simplement pas de phrase, donc `null`.
 */
export async function detailTextAsync(err) {
    const corps = err && err.response ? err.response.data : (err ? err.data : undefined);
    if (typeof Blob !== 'undefined' && corps instanceof Blob) {
        try {
            const parsed = JSON.parse(await blobText(corps));
            return typeof parsed?.detail === 'string' && parsed.detail.trim() ? parsed.detail : null;
        } catch {
            return null;
        }
    }
    return detailText(err);
}

/** Le texte d'un Blob — `Blob.text()` quand il existe, `FileReader` sinon.
 *
 *  ⚠ Le repli n'est pas décoratif : jsdom (donc TOUTE la suite de tests) livre
 *  un `Blob` SANS `.text()`. Le déballage écrit en face de `.text()` seul est
 *  donc du code que rien ne peut exercer — il passait au vert parce que son
 *  `catch` avalait le `TypeError` et rendait `null`, c'est-à-dire exactement
 *  ce qu'un Blob illisible rend. Une garde qu'on ne peut pas voir échouer ne
 *  garde rien. */
function blobText(blob) {
    if (typeof blob.text === 'function') return blob.text();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error || new Error('blob unreadable'));
        reader.readAsText(blob);
    });
}
